import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { LanguageToggle } from "./LanguageToggle";
import { CONTACT } from "../data/content";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "./ui/sheet";

const NAV_LINKS = [
  { key: "home",     to: "/" },
  { key: "about",    to: "/about" },
  { key: "services", to: "/programs" },
  { key: "learn",    to: "/learn" },
  { key: "crm",      to: "/platform" },
  { key: "partners", to: "/partners" },
  { key: "faq",      to: "/faq" },
  { key: "careers",  to: "/careers" },
  { key: "contact",  to: "/contact" },
];

export const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-100 shadow-sm"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-4">
          <Logo className="h-8 sm:h-9 shrink-0" />

          {/* Desktop nav — spreads evenly between the logo and the right
              cluster so the header looks the same in EN and FR even though
              French labels are 20–40% wider. */}
          <nav
            className="hidden lg:flex flex-1 items-center justify-between px-4 xl:px-8"
            data-testid="desktop-nav"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.key}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide whitespace-nowrap transition-colors duration-200 ${
                    isActive
                      ? "text-brand-navy"
                      : "text-slate-500 hover:text-brand-navy"
                  }`
                }
              >
                {t(`nav.${link.key}`)}
              </NavLink>
            ))}
          </nav>

          {/* Desktop right cluster */}
          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <LanguageToggle />
            <Link
              to="/about"
              data-testid="nav-cta-desktop"
              className="inline-flex items-center rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-6 py-2.5 text-sm font-semibold transition-colors duration-200 shadow-sm whitespace-nowrap"
            >
              {t("nav.cta")}
            </Link>
          </div>

          {/* Mobile menu */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageToggle />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-full text-brand-navy hover:bg-brand-mist transition-colors"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="mobile-menu-panel"
                className="w-[86vw] max-w-sm bg-[#FBF9F5] border-0 p-0 flex flex-col [&>button]:hidden"
              >
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <div className="flex items-center justify-end px-7 h-20">
                  <SheetClose asChild>
                    <button
                      data-testid="mobile-menu-close"
                      aria-label="Close menu"
                      className="inline-flex items-center justify-center h-10 w-10 rounded-full text-brand-navy/70 hover:text-brand-navy hover:bg-brand-navy/5 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </SheetClose>
                </div>

                <p className="px-7 mt-2 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-teal">
                  {t("nav.menu")}
                </p>

                <nav className="flex-1 flex flex-col px-7 mt-4" data-testid="mobile-nav">
                  {NAV_LINKS.map((link, i) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      data-testid={`mobile-nav-link-${link.key}`}
                      className={({ isActive }) =>
                        `group flex items-baseline gap-4 py-3 transition-transform duration-200 hover:translate-x-1 ${
                          isActive ? "text-brand-teal" : "text-brand-navy"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            className={`text-xs font-semibold tabular-nums pt-1 ${
                              isActive ? "text-brand-teal" : "text-brand-teal/40"
                            }`}
                          >
                            0{i + 1}
                          </span>
                          <span className="text-lg leading-none font-heading font-semibold tracking-tight">
                            {t(`nav.${link.key}`)}
                          </span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </nav>

                <div className="px-7 pb-8 pt-4 border-t border-black/5 space-y-3">
                  <LanguageToggle variant="mobile" />
                  <Link
                    to="/contact"
                    data-testid="mobile-nav-cta"
                    className="flex items-center justify-center rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-6 py-3.5 text-base font-semibold transition-colors"
                  >
                    {t("nav.mobileCta")}
                  </Link>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="mobile-nav-phone"
                    className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-brand-navy transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {CONTACT.phone}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
